import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckSquare, RefreshCw, Square, Trash2, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

/**
 * Modal that lists domain assignments whose domain has not received any
 * visitor traffic in the last N days, so an admin can reclaim them in bulk
 * and free pool capacity.
 */
export const IdleAssignmentsModal = ({ api, headers, open, onClose, onReclaimed }) => {
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [onlyUnlicensed, setOnlyUnlicensed] = useState(false);

  const fetchIdle = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${api}/admin/domains/idle-assignments?days=${days}`, { headers });
      setItems(data.items || []);
      setSelected(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load idle assignments");
    } finally {
      setLoading(false);
    }
  }, [api, headers, days]);

  useEffect(() => {
    if (open) fetchIdle();
  }, [open, fetchIdle]);

  const filtered = useMemo(() => {
    return onlyUnlicensed ? items.filter((i) => !i.is_licensed || !i.user_exists) : items;
  }, [items, onlyUnlicensed]);

  const allChecked = filtered.length > 0 && filtered.every((i) => selected.has(i.assignment_id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(filtered.map((i) => i.assignment_id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const reclaim = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Free ${selected.size} assignment(s)? This removes their links and returns the domain(s) to the shared pool.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${api}/admin/domains/idle-assignments/bulk-unassign`,
        { assignment_ids: Array.from(selected) },
        { headers }
      );
      toast.success(`Freed ${data.unassigned} assignment(s)`);
      await fetchIdle();
      if (onReclaimed) onReclaimed();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reclaim failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 pt-10" data-testid="idle-assignments-modal">
      <div className="w-full max-w-4xl rounded-lg border border-slate-800 bg-[#0C121D] shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">Reclaim capacity</p>
            <h2 className="mt-1 text-lg font-semibold text-slate-100">Idle Domain Assignments</h2>
            <p className="mt-1 text-xs text-slate-500">Assignments whose domain has had zero visitor traffic in the selected window.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="idle-close-btn" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 px-5 py-3">
          <label className="text-xs uppercase text-slate-500">Window</label>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="input-dark w-32" data-testid="idle-days-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>

          <button
            type="button"
            onClick={fetchIdle}
            disabled={loading}
            className="btn-secondary flex items-center gap-2 disabled:opacity-40"
            data-testid="idle-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Rescan
          </button>

          <label className="ml-auto flex items-center gap-2 text-xs text-slate-400" data-testid="idle-unlicensed-toggle">
            <input
              type="checkbox"
              checked={onlyUnlicensed}
              onChange={(e) => setOnlyUnlicensed(e.target.checked)}
              className="accent-amber-400"
            />
            Only unlicensed / deleted users
          </label>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="px-5 py-16 text-center text-sm text-slate-500" data-testid="idle-loading">Loading idle assignments...</div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-16 text-center text-sm text-slate-500" data-testid="idle-empty">
              <CheckSquare className="mx-auto h-6 w-6 text-emerald-400" />
              <p className="mt-2">No idle assignments in this window.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0C121D] text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">
                    <button type="button" onClick={toggleAll} className="flex items-center gap-2" data-testid="idle-toggle-all">
                      {allChecked ? <CheckSquare className="h-4 w-4 text-cyan-400" /> : <Square className="h-4 w-4" />}
                      <span className="text-[10px]">{selected.size}/{filtered.length}</span>
                    </button>
                  </th>
                  <th className="px-4 py-2 text-left">Domain</th>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Since</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((item) => {
                  const checked = selected.has(item.assignment_id);
                  const risky = !item.user_exists || !item.is_licensed;
                  return (
                    <tr key={item.assignment_id} className={checked ? "bg-cyan-500/5" : "hover:bg-slate-900/50"} data-testid={`idle-row-${item.assignment_id}`}>
                      <td className="px-4 py-2">
                        <button type="button" onClick={() => toggleOne(item.assignment_id)} aria-label={`Toggle ${item.domain}`}>
                          {checked ? <CheckSquare className="h-4 w-4 text-cyan-400" /> : <Square className="h-4 w-4 text-slate-500" />}
                        </button>
                      </td>
                      <td className="px-4 py-2 font-mono text-slate-100">{item.domain}</td>
                      <td className="px-4 py-2 text-slate-300">
                        {item.username}
                        {item.email && <span className="ml-1 text-xs text-slate-500">· {item.email}</span>}
                      </td>
                      <td className="px-4 py-2">
                        {!item.user_exists ? (
                          <span className="rounded border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-300">Deleted user</span>
                        ) : !item.is_licensed ? (
                          <span className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-300">Unlicensed</span>
                        ) : (
                          <span className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[10px] text-slate-400">Licensed · idle</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">{item.assigned_at ? new Date(item.assigned_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-800 px-5 py-4">
          <p className="flex items-center gap-2 text-xs text-amber-300" data-testid="idle-warning">
            <AlertTriangle className="h-4 w-4" />
            Freeing an assignment removes the user's links on that domain and returns it to the shared pool.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="idle-cancel-btn">Cancel</button>
            <button
              type="button"
              onClick={reclaim}
              disabled={!selected.size || busy}
              className="flex items-center gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300 disabled:opacity-40"
              data-testid="idle-reclaim-btn"
            >
              <Trash2 className="h-4 w-4" />
              {busy ? "Freeing..." : `Free ${selected.size} assignment${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};
